import { ReactElement, ReactNode } from "react";
import { T } from "react-translator-component";
import {
  Box, Button, Modal,
  Stack, SxProps
} from "@mui/material";
import { colors } from "../../common/colors";
import ContainerPaper from "./ContainerPaper";
import SafeTextWithLinks from "./SafeTextWithLinks";

type Props = {
  open: boolean;
  title?: string | ReactNode;
  subtitle?: string;
  buttonLabel?: string;
  secondButton?: { // 設定されている場合にボタンを２つ表示する
    label: string;
    disabled?: boolean;
    onClick: (event: any) => void;
  }
  sx?: SxProps;
  children: ReactNode;
  highlightWords?: string[];
  onClose: () => void;
  onModalClose?: () => void; // onClose と区別する必要がある場合
}

/**
 * title に ReactNode を指定した場合に利用してもらうスタイル
 */
export const sxTitle = {
  fontSize: 18,
  fontWeight: "600",
  textAlign: "center"
}

/**
 * 何か表示するためのモーダル。
 * 入力欄がありデータ送信が発生するようなケースは FormDialog 側を使用すること。
 */
export default function DisplayModal({ open, title, subtitle, buttonLabel, secondButton, sx, children, onClose, onModalClose, highlightWords }: Props): ReactElement {
  return (
    <Modal open={open} onClose={onModalClose ? onModalClose : onClose}>
      <ContainerPaper sx={{ ...styles.container, ...sx }}>
        {typeof title === 'string' && (
          <SafeTextWithLinks
            text={title}
            highlightWords={highlightWords}
            sx={sxTitle}
          />
        )}
        {/*
          ReactNode の場合、ハイライト対象のテキストが書かれていることを知っているのはこれを渡してきたコンポーネントのみ。
          そのため、ここでハイライトはしない。
          (このコンポーネントやSafeTextWithLinkでDOM解析まですると責務が大きくなりすぎる)
        */}
        {title && typeof title !== 'string' && (
          title
        )}
        {subtitle && (
          <SafeTextWithLinks
            text={subtitle}
            highlightWords={highlightWords}
            sx={styles.subtitle}
          />
        )}
        { children }
        {secondButton ? (
          <Box sx={styles.doubleButtons.container}>
            <Button
              onClick={secondButton.onClick}
              disabled={secondButton.disabled}
              sx={styles.doubleButtons.secondButton}
            >
              {secondButton.label}
            </Button>
            <Button
              onClick={onClose}
              sx={styles.doubleButtons.defaultButton}
            >
              {buttonLabel ?? T('mng_close')}
            </Button>
          </Box>
        ) : (
          <Stack sx={styles.singleButton}>
            <Button
              onClick={onClose}
              size={"large"}
              sx={{ ...styles.singleButton.button, ...sx}}
              variant="outlined"
            >
              {buttonLabel ?? T('mng_close')}
            </Button>
          </Stack>
        )}
      </ContainerPaper>
    </Modal>
  )
}

const styles = {
  container: {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "max-content",
    maxHeight: "calc(100vh * 0.9)",
    borderRadius: "15px",
    boxShadow: 24,
    margin: 0,
    paddingTop: 6,
    overflow: "hidden"
  },
  subtitle: {
    textAlign: "center",
    marginTop: 1
  },
  doubleButtons: {
    container: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: 3,
      marginBottom: 0,
    },
    defaultButton: {
      cursor: "pointer",
      width: "50%",
      textAlign: "center",
      border: "0.5px solid gray",
      borderRight: "none",
      borderBottom: "none",
      borderRadius: 0,
      padding: "10px",
      color: colors.farm_blue200,
      fontWeight: "bold",
    },
    secondButton: {
      cursor: "pointer",
      width: "50%",
      textAlign: "center",
      border: "0.5px solid gray",
      borderRight: "none",
      borderBottom: "none",
      borderRadius: 0,
      padding: "10px",
      color: colors.farm_blue200,
      fontWeight: "bold",
      borderLeft: "none"
    },
  },
  singleButton: {
    alignItems: "center",
    justifyContent: "center",
    marginTop: "30px",
    marginBottom: "20px",
    button: {
      width: "50%",
      minWitdh: "100px",
      alignSelf: "center",
      display: "flex",
      paddingY: 1,
      border: "3px solid",
      borderRadius: "10px",
      borderColor: colors.farm_blue200,
      color: colors.farm_black,
      "&:hover": {
        border: "3px solid",
        borderColor: colors.farm_blue200,
        color: colors.farm_black
      }
    }
  }
}
